import { NavigationHandlerParams, ForceUpdateParams } from "./types";
import { StatusEnum } from "../../../endpoints/getArtifacts";
import { useNavigate } from "react-router-dom";
import { useUpdateAppealMutation } from "../../../endpoints/updateAppeal";
import { useCallback, useEffect } from "react";

export const handleBackToProperty = async ({
    data,
    propertyId,
    alias,
    updateAppeal,
    navigate,
}: NavigationHandlerParams) => {
    try {
        await updateAppeal({
            businessUseCase: "SMART",
            appealArtifactId: data.appealArtifactId,
            appealDetails: data.appealDetails,
            appealStatus: StatusEnum.UNDER_APPEAL,
            clientName: alias,
        });
        navigate(`/property-details/${propertyId}`);
    } catch (error) {
        console.error("Error updating appeal status:", error);
    }
};

export const handleForceUpdate = async ({
    data,
    propertyId,
    alias,
    updateAppeal,
    navigate,
    current_url,
}: ForceUpdateParams) => {
    if (
        data?.appealDetails?.status === StatusEnum.EDITING.toString() &&
        data?.appealDetails?.editingUser === alias
    ) {
        try {
            await updateAppeal({
                businessUseCase: "SMART",
                appealArtifactId: data.appealArtifactId,
                appealDetails: {},
                appealStatus: StatusEnum.UNDER_APPEAL,
                clientName: alias,
            });
            if (current_url) {
                localStorage.setItem("page", current_url);
                navigate(`/property-details/${propertyId}`);
            }
        } catch (error) {
            console.error("Error updating details:", error);
        }
    }
};

export const handleForceUpdateWithoutNavigation = async ({
    data,
    alias,
    updateAppeal,
}: Partial<NavigationHandlerParams>) => {
    if (
        data?.appealDetails?.status === StatusEnum.EDITING.toString() &&
        data?.appealDetails?.editingUser === alias
    ) {
        try {
            await updateAppeal({
                businessUseCase: "SMART",
                appealArtifactId: data.appealArtifactId,
                appealDetails: {},
                appealStatus: StatusEnum.UNDER_APPEAL,
                clientName: alias,
            });
        } catch (error) {
            console.error("Error updating details:", error);
        }
    }
};

export const useNavigationHandlers = (
    data: any,
    alias: string,
    propertyId: string,
    current_url: string
) => {
    const navigate = useNavigate();
    const [updateAppeal] = useUpdateAppealMutation();

    const doForceUpdate = useCallback(() => {
        return handleForceUpdate({
            data,
            propertyId,
            alias,
            updateAppeal,
            navigate,
            current_url,
        });
    }, [data, propertyId, alias, updateAppeal, navigate, current_url]);

    const doNavUpdate = useCallback(() => {
        return handleForceUpdateWithoutNavigation({
            data,
            alias,
            updateAppeal,
        });
    }, [data, alias, updateAppeal]);

    return { doForceUpdate, doNavUpdate: doNavUpdate };
};

export const useUnloadHandler = (
    data: any,
    alias: string,
    current_url: string,
    propertyId: string,
    doForceUpdate: () => void
) => {
    const navigate = useNavigate();

    useEffect(() => {
        if (sessionStorage.getItem("page") === current_url) {
            sessionStorage.removeItem("page");
            navigate(`/property-details/${propertyId}`);
        }

        const handleBeforeUnload = (event: BeforeUnloadEvent) => {
            if (
                data?.appealDetails?.status === StatusEnum.EDITING.toString() &&
                data?.appealDetails?.editingUser === alias
            ) {
                const message = "Please restart the Export process";
                event.preventDefault();
                event.returnValue = message;
                sessionStorage.setItem("page", current_url);
                doForceUpdate();

                return message;
            }
        };

        window.addEventListener("beforeunload", handleBeforeUnload);

        return () => window.removeEventListener("beforeunload", handleBeforeUnload);
    }, [data, alias, current_url, propertyId, navigate, doForceUpdate]);
};
